import { notification, TableProps } from "antd";
import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";
import { onError } from "src/common/onError";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { EntityCardStore } from "src/pages/EntityCardPage/EntityCardStore";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { ZUid } from "src/types/zUid";
import {
  SchedFunctionType,
  TaskDeletionError,
  ZColumnSettings,
  zColumnSettings,
  ZSchedulingPlan,
  ZSchedulingRow,
  ZSchedulingSettings,
  ZTasksDeletionCheckInfo,
} from "../SchedulingTypes";
import {
  checkDeleteTasks,
  createTask,
  deleteTasks,
  loadSchedulingData,
  loadSchedulingSettings,
  saveSchedStartDate,
} from "../apiScheduling";
import { searchTasks } from "./SchedulingTable/SchedTableToolbar/TaskSearch/searchTasks";
import { buildColumns } from "./SchedulingTable/buildColumns";
import { DeletionTRow } from "./SchedulingTable/SchedDelToolbar/DeletionInfoTable/buildDelTableColumns";

export type TaskEditor = {
  cardStore: EntityCardStore;
  title: string;
};

export type TemplateCreator = {
  filterStore: EntityFiltersPageStore;
  fnCreate: (
    entityId: ZUid,
    templateId: ZUid,
    objectServiceId: ZUid,
  ) => Promise<void>;
  title: string;
};

const buildColumnSettingsKey = (entityId: ZUid) =>
  `schedulingSettings-${entityId}`;

export const schedulingControlStore = makeAutoObservable({
  data: { status: "none" } as RemoteData<ZSchedulingRow[]>,
  setData(data: RemoteData<ZSchedulingRow[]>) {
    this.data = data;
  },

  settings: null as ZSchedulingSettings | null,
  setSettings(settings: ZSchedulingSettings | null) {
    this.settings = settings;
  },

  entityId: null as ZUid | null,
  setEntityId(id: ZUid) {
    this.entityId = id;
  },

  objectServiceId: null as ZUid | null,
  setObjectServiceId(id: ZUid) {
    this.objectServiceId = id;
  },

  get functions(): SchedFunctionType[] {
    return this.schedulingPlan?.functions ?? [];
  },

  schedulingPlan: null as ZSchedulingPlan | null,
  setSchedulingPlan(data: ZSchedulingPlan | null) {
    this.schedulingPlan = data;
  },

  get availableFunctions(): Set<SchedFunctionType> {
    return new Set(this.functions);
  },

  async init(entityId: ZUid, objectServiceId: ZUid) {
    try {
      this.setEntityId(entityId);
      this.setObjectServiceId(objectServiceId);
      this.hiddenColumns.clear();
      this.setSelectedRows([]);
      this.setSettings(null);
      this.setSearchQuery("");
      this.setColumnSettingsKey(buildColumnSettingsKey(entityId));
      this.loadColumnSettings();
      this.loadData();
    } catch (error) {
      onError(error);
    }
  },

  async loadData() {
    const { entityId, settings, objectServiceId } = this;
    try {
      if (!entityId) {
        throw Error(t("Missing value", { parameter: "entityId" }));
      }
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      this.setData({ status: "wait" });
      if (!settings) {
        const newSettings = await loadSchedulingSettings(objectServiceId);
        if (!newSettings.taskSettings)
          throw Error(t("The service has not been configured"));
        this.setSettings(newSettings);
      }
      const result = await loadSchedulingData(entityId, objectServiceId);
      this.setData({ status: "ready", result: [result.plan] });
      this.setSchedulingPlan(result);
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },

  get projectInfo(): ZSchedulingRow | null {
    if (this.data.status !== "ready") return null;
    return this.data.result.find(({ type }) => type === "PLAN") ?? null;
  },

  operationLoading: false,
  setOperationLoading(flag: boolean) {
    this.operationLoading = flag;
  },

  async changeStartDate(date: string) {
    const { objectServiceId, entityId } = this;
    try {
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      if (!entityId) {
        throw Error(t("Missing value", { parameter: "entityId" }));
      }
      this.setOperationLoading(true);
      await saveSchedStartDate(entityId, date, objectServiceId);
      this.loadData();
    } catch (error) {
      onError(error);
    } finally {
      this.setOperationLoading(false);
    }
  },

  // table

  get columns(): TableProps["columns"] {
    const { settings } = this;
    return settings ? buildColumns(settings) : [];
  },

  get visibleColumns(): TableProps["columns"] {
    return this.columns?.filter(
      ({ key }) => !this.hiddenColumns.has(String(key)),
    );
  },

  hiddenColumns: new Set<ZUid>(),
  toggleColumnVisibility(key: ZUid) {
    const { hiddenColumns } = this;
    if (hiddenColumns.has(key)) {
      hiddenColumns.delete(key);
    } else {
      hiddenColumns.add(key);
    }
    this.saveColumnSettings();
  },

  get dataSource(): ZSchedulingRow[] {
    const { data, searchQuery } = this;
    if (data.status === "ready") {
      return searchQuery ? searchTasks(data.result, searchQuery) : data.result;
    }
    return [];
  },

  selectedRows: [] as ZSchedulingRow[],
  setSelectedRows(rowKeys: ZSchedulingRow[]) {
    this.selectedRows = rowKeys;
  },

  get selectedRowKeys(): ZUid[] {
    return this.selectedRows.map(({ id }) => id);
  },

  get selectedTaskIds(): ZUid[] {
    return this.selectedRows
      .filter(({ type }) => type === "TASK")
      .map(({ id }) => id);
  },

  get selectedTasks(): Set<ZUid> {
    return new Set(this.selectedTaskIds);
  },

  searchQuery: "",
  setSearchQuery(query: string) {
    this.searchQuery = query;
  },

  columnWidths: {} as Record<string, number | string>,
  setColumnWidth(key: string, width: number | string) {
    this.columnWidths[key] = width;
    this.saveColumnSettings();
  },

  columnSettingsKey: null as string | null,
  setColumnSettingsKey(key: string | null) {
    this.columnSettingsKey = key;
  },

  loadColumnSettings() {
    const { columnSettingsKey } = this;
    try {
      if (!columnSettingsKey)
        throw Error(t("Missing value", { parameter: "columnSettingsKey" }));
      const settings = localStorage.getItem(columnSettingsKey);
      if (!settings) return;
      const json = JSON.parse(settings);
      const { hiddenColumns, columnWidths } = zColumnSettings.parse(json);
      hiddenColumns?.forEach((col) => this.toggleColumnVisibility(col));
      Object.entries(columnWidths ?? {}).forEach(([key, width]) =>
        this.setColumnWidth(key, width),
      );
    } catch (error) {
      onError(error);
    }
  },

  saveColumnSettings() {
    const { columnSettingsKey } = this;
    try {
      if (!columnSettingsKey)
        throw Error(t("Missing value", { parameter: "columnSettingsKey" }));
      const settings: ZColumnSettings = {
        hiddenColumns: Array.from(this.hiddenColumns),
        columnWidths: this.columnWidths,
      };
      localStorage.setItem(columnSettingsKey, JSON.stringify(settings));
    } catch (error) {
      onError(error);
    }
  },

  // task edit/create

  editor: null as TaskEditor | null,
  setEditor(ed: TaskEditor | null) {
    this.editor = ed;
  },

  openEditor(row: ZSchedulingRow) {
    const { id, type } = row;
    if (type !== "TASK") return;
    const cardStore = new EntityCardStore();
    cardStore.init(String(id));
    this.setEditor({ cardStore, title: row.name?.value ?? "" });
  },

  closeEditor() {
    this.setEditor(null);
  },

  openCreation(taskObjId: ZUid) {
    const { entityId, objectServiceId } = this;
    try {
      if (!entityId) {
        throw Error(t("Missing value", { parameter: "entityId" }));
      }
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      const cardStore = new EntityCardStore({
        fnCreate: (ent) => createTask(entityId, ent, objectServiceId),
      });
      this.setEditor({ cardStore, title: t("New task") });
      cardStore.initNew(taskObjId);
    } catch (error) {
      onError(error);
    }
  },

  onUpdate() {
    this.closeEditor();
    this.loadData();
  },

  // template-creation

  templateCreator: null as TemplateCreator | null,
  setTemplateCreator(ed: TemplateCreator | null) {
    this.templateCreator = ed;
  },

  closeTemplateCreator() {
    this.setTemplateCreator(null);
  },

  openTemplateCreation(
    templateId: ZUid,
    title: string,
    fnCreate: (
      entityId: ZUid,
      templateId: ZUid,
      objectServiceId: ZUid,
    ) => Promise<void>,
  ) {
    this.setTemplateCreator({
      filterStore: new EntityFiltersPageStore({
        actions: new Set([]),
        objectId: templateId,
        selectionSettings: { selectionType: "radio" },
        onRowClick: (row) => {
          const tableStore = this.templateCreator?.filterStore.tableStore;
          if (!tableStore) return;
          selectOnRowClick(row, tableStore);
        },
      }),
      title,
      fnCreate,
    });
  },

  async onTemplateCreate() {
    const { templateCreator, entityId, objectServiceId } = this;
    const templateEntityId =
      templateCreator?.filterStore.tableStore?.selected[0]?.id;
    try {
      if (!entityId) {
        throw Error(t("Missing value", { parameter: "entityId" }));
      }
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      if (!templateEntityId)
        throw Error(t("Missing value", { parameter: "templateEntityId" }));
      this.setOperationLoading(true);
      await templateCreator.fnCreate(
        entityId,
        templateEntityId,
        objectServiceId,
      );
      this.loadData();
    } catch (error) {
      onError(error);
    } finally {
      this.setOperationLoading(false);
      this.closeTemplateCreator();
    }
  },

  // delete

  taskDeletion: null as ZTasksDeletionCheckInfo | null,
  setTaskDeletion(deletion: ZTasksDeletionCheckInfo | null) {
    this.taskDeletion = deletion;
  },

  safeData: null as ZSchedulingRow[] | null,
  setSafeData(data: ZSchedulingRow[] | null) {
    this.safeData = data;
  },

  getDelMessage(name?: string, type?: TaskDeletionError | null): string {
    const deletionMessages: Record<TaskDeletionError, string> = {
      [TaskDeletionError.notFound]: t("Task was not found", { name }),
      [TaskDeletionError.inProgress]: t("Task is in progress", { name }),
      [TaskDeletionError.finished]: t("Task is completed", { name }),
    };
    return type ? deletionMessages[type] : "";
  },

  async checkDelete() {
    const { selectedTaskIds, entityId, objectServiceId, data } = this;
    try {
      if (!entityId) {
        throw Error(t("Missing value", { parameter: "entityId" }));
      }
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      this.setOperationLoading(true);
      const { checkDelete, plan } = await checkDeleteTasks(
        entityId,
        selectedTaskIds,
        objectServiceId,
      );
      if (checkDelete.canDelete) {
        this.setTaskDeletion(checkDelete);
        if (data.status === "ready") {
          this.setSafeData(data.result);
        }
        this.setData({ status: "ready", result: [plan] });
      } else {
        const isMultiDeletion = checkDelete.tasks.length > 1;
        const firstDeletedTask = checkDelete.tasks[0];
        notification.error({
          message: t(
            isMultiDeletion
              ? "The selected tasks contain tasks in the “In Progress” and/or “Completed” status!"
              : this.getDelMessage(
                  firstDeletedTask?.name ?? "",
                  firstDeletedTask?.error,
                ),
          ),
          description: t(
            isMultiDeletion
              ? "You cannot delete tasks. Select tasks in the “Created” state."
              : t("You cannot delete a task."),
          ),
        });
      }
    } catch (error) {
      onError(error);
    } finally {
      this.setOperationLoading(false);
    }
  },

  async doDelete() {
    const { selectedTaskIds, entityId, objectServiceId, taskDeletion } = this;
    try {
      if (!entityId) {
        throw Error(t("Missing value", { parameter: "entityId" }));
      }
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      this.setOperationLoading(true);
      this.setData({
        status: "ready",
        result: [await deleteTasks(entityId, selectedTaskIds, objectServiceId)],
      });
      this.setTaskDeletion(null);
      const isMultiDeletion = selectedTaskIds.length > 1;
      const firstDeletedTask = taskDeletion?.tasks.find(
        ({ id }) => id === selectedTaskIds[0],
      )?.name;
      notification.success({
        message: isMultiDeletion
          ? t("Tasks have been deleted.")
          : t("Task has been deleted.", { name: firstDeletedTask }),
        description: t(
          "Task bindings associated with the task(s) have been deleted. Scheduled start and end dates of tasks recalculated.",
        ),
      });
      this.setSelectedRows([]);
    } catch (error) {
      onError(error);
    } finally {
      this.setOperationLoading(false);
    }
  },

  cancelDeletion() {
    const { safeData } = this;
    try {
      if (!safeData) {
        throw Error(t("Missing value", { parameter: "safeData" }));
      }
      this.setTaskDeletion(null);
      this.setData({ status: "ready", result: safeData });
      this.setSafeData(null);
    } catch (error) {
      onError(error);
    }
  },

  get delTableData(): DeletionTRow[] {
    const { taskDeletion } = this;
    if (!taskDeletion) return [];
    return taskDeletion.tasks.map((task, index) => ({
      ...task,
      ...(index === 0 && {
        recalculatedTasks: taskDeletion.recalculatedTasks,
        rowSpan: taskDeletion.tasks.length,
      }),
    }));
  },
});

export type SchedulingControlStore = typeof schedulingControlStore;
